import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HEALTH_STATUS } from 'models/fleet-management/FleetWidget';
import { FleetDataRobots } from 'models/fleet-management/FleetDataSet';
import DashboardSection from '../DashboardSection';
import MeterTrack from './MeterTrack';
import {
  CHART_SERIES_BLUE,
  FLEET_SECTION_GAP,
  HEALTH_MARK_COLORS,
  HEALTH_STATUS_LABEL_KEYS,
  HEALTH_STATUS_ORDER,
  HEALTH_TONES,
} from 'utils/fleet-dashboard-theme';

type HealthCounts = Record<Exclude<HEALTH_STATUS, HEALTH_STATUS.ALL>, number>;

interface DealerStat {
  dealer: string;
  robotCount: number;
  greenPercentage: number;
  orangePercentage: number;
  redPercentage: number;
  statusCounts: { total: number } & HealthCounts;
}


/** The column widths every row and the header share, so the two lists line up. */
const NAME_COLUMN = { flex: '1 1 40%', minWidth: 0 };
const METER_COLUMN = { flex: '1 1 45%', minWidth: 72, maxWidth: 240 };
const SHARE_COLUMN = 60;
const COUNT_COLUMN = 40;

const HeaderCell: React.FC<{ width?: number; children: React.ReactNode }> = ({
  width,
  children,
}) => (
  <Typography
    variant="caption"
    color="text.secondary"
    width={width}
    textAlign={width ? 'right' : undefined}
    sx={width ? undefined : NAME_COLUMN}
  >
    {children}
  </Typography>
);

const DealerOverviewWidgets: React.FC<{
  robotsData: FleetDataRobots[];
}> = ({ robotsData }) => {
  const { t } = useTranslation();

  const [healthExpanded, setHealthExpanded] = useState(false);
  const [countExpanded, setCountExpanded] = useState(false);

  const dealerStats = useMemo(() => {
    const dealerStats: Record<string, { total: number } & HealthCounts> = {};

    for (const robot of robotsData) {
      const dealer = robot.dealer;
      if (!dealer) continue;

      if (!dealerStats[dealer]) {
        dealerStats[dealer] = { total: 0, GREEN: 0, ORANGE: 0, RED: 0, GREY: 0 };
      }

      dealerStats[dealer].total += 1;
      const healthStatus = robot.customer_journey_status as
        Exclude<HEALTH_STATUS, HEALTH_STATUS.ALL> | undefined;
      if (healthStatus && healthStatus in dealerStats[dealer]) {
        dealerStats[dealer][healthStatus] += 1;
      }
    }

    const dealers = Object.entries(dealerStats).map(([dealer, stats]) => {
      const total = stats.total || 1;

      return {
        dealer,
        robotCount: total,
        greenPercentage: (stats.GREEN / total) * 100,
        orangePercentage: (stats.ORANGE / total) * 100,
        redPercentage: (stats.RED / total) * 100,
        statusCounts: stats,
      };
    });

    const byHealth = [...dealers]
      .sort(
        (a, b) =>
          b.greenPercentage - a.greenPercentage ||
          b.orangePercentage - a.orangePercentage ||
          b.redPercentage - a.redPercentage,
      );
    const byCount = [...dealers].sort((a, b) => b.robotCount - a.robotCount);
    return { byHealth, byCount };
  }, [robotsData]);

  const expandByHealthListClickHandler = () => {
    setHealthExpanded((expanded) => !expanded);
  }

  const expandByCountListClickHandler = () => {
    setCountExpanded((expanded) => !expanded);
  }

  const renderHealthRow = (d: DealerStat) => {
    const total = d.robotCount || 1;
    const shares = HEALTH_STATUS_ORDER.map((status) => ({
      status,
      percentage: Math.round((d.statusCounts[status] / total) * 100),
    }));
    const breakdown = HEALTH_STATUS_ORDER.map(
      (status) => `${t(HEALTH_STATUS_LABEL_KEYS[status])} ${d.statusCounts[status]}`,
    ).join(' · ');

    return (
      <Tooltip key={d.dealer} title={breakdown} placement="top" followCursor>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="body2" color="text.primary" noWrap sx={NAME_COLUMN}>
            {d.dealer}
          </Typography>
          <MeterTrack>
            {shares
              .filter((share) => share.percentage > 0)
              .map((share) => (
                <Box
                  key={share.status}
                  sx={{
                    flex: `0 0 ${share.percentage}%`,
                    backgroundColor: HEALTH_MARK_COLORS[share.status],
                  }}
                />
              ))}
          </MeterTrack>
          {/* The one number the list is sorted on, so it wears the healthy tone. */}
          <Box width={SHARE_COLUMN} display="flex" justifyContent="flex-end">
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                px: 0.75,
                borderRadius: 1,
                backgroundColor: HEALTH_TONES[HEALTH_STATUS.GREEN].tint,
                color: HEALTH_TONES[HEALTH_STATUS.GREEN].ink,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {`${Math.round((d.statusCounts.GREEN / total) * 100)}%`}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            width={COUNT_COLUMN}
            textAlign="right"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {d.robotCount}
          </Typography>
        </Stack>
      </Tooltip>
    );
  };

  const renderCountRow = (d: DealerStat) => {
    const maxCount = dealerStats.byCount[0]?.robotCount || 1;
    const barWidth = Math.round((d.robotCount / maxCount) * 100);

    return (
      <Stack key={d.dealer} direction="row" alignItems="center" gap={1.5}>
        <Typography variant="body2" color="text.primary" noWrap sx={NAME_COLUMN}>
          {d.dealer}
        </Typography>
        <MeterTrack>
          <Box
            sx={{
              flex: `0 0 ${barWidth}%`,
              borderRadius: 1,
              backgroundColor: CHART_SERIES_BLUE,
            }}
          />
        </MeterTrack>
        <Typography
          variant="body2"
          color="text.secondary"
          width={COUNT_COLUMN}
          textAlign="right"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {d.robotCount}
        </Typography>
      </Stack>
    );
  };

  const emptyState = (
    <Typography variant="body2" color="text.secondary">
      {t('fleet-management-no-data')}
    </Typography>
  );

  /** The four statuses named once for the whole card, so no row has to repeat them. */
  const healthLegend = (
    <Stack direction="row" gap={1.5} flexWrap="wrap">
      {HEALTH_STATUS_ORDER.map((status) => (
        <Stack
          key={status}
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: HEALTH_TONES[status].tint,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: HEALTH_MARK_COLORS[status],
            }}
          />
          <Typography variant="caption" fontWeight={600} color={HEALTH_TONES[status].ink}>
            {t(HEALTH_STATUS_LABEL_KEYS[status])}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack gap={1.5} width="100%">
      <Typography variant="subtitle2" color="text.secondary">
        {t('fleet-management-title-dealer-overview')}
      </Typography>

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        gap={FLEET_SECTION_GAP}
        alignItems="stretch"
        width="100%"
      >
        <DashboardSection
          title={t('fleet-management-subtitle-top-dealers')}
          caption={t('fleet-management-caption-by-health-status')}
          actions={healthLegend}
          sx={{ flex: '1 1 0' }}
        >
          <Stack gap={1}>
            {dealerStats.byHealth.length === 0 && emptyState}
            {dealerStats.byHealth.length > 0 && (
              <Stack direction="row" alignItems="center" gap={1.5}>
                <HeaderCell>{t('fleet-management-field-dealer')}</HeaderCell>
                <Box sx={METER_COLUMN} />
                <HeaderCell width={SHARE_COLUMN}>
                  {t('fleet-management-column-healthy-share')}
                </HeaderCell>
                <HeaderCell width={COUNT_COLUMN}>{t('fleet-management-column-robots')}</HeaderCell>
              </Stack>
            )}
            {dealerStats.byHealth.slice(0, 15).map((d) => renderHealthRow(d))}
            <Collapse in={healthExpanded}>
              <Stack gap={1}>{dealerStats.byHealth.slice(15).map((d) => renderHealthRow(d))}</Stack>
            </Collapse>
            {dealerStats.byHealth.slice(15).length > 0 && (
              <Button
                variant="text"
                size="small"
                endIcon={healthExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={expandByHealthListClickHandler}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t(healthExpanded ? 'fleet-management-show-less' : 'fleet-management-show-all')}
              </Button>
            )}
          </Stack>
        </DashboardSection>

        <DashboardSection
          title={t('fleet-management-subtitle-top-dealers')}
          caption={t('fleet-management-caption-by-robot-count')}
          sx={{ flex: '1 1 0' }}
        >
          <Stack gap={1}>
            {dealerStats.byCount.length === 0 && emptyState}
            {dealerStats.byCount.length > 0 && (
              <Stack direction="row" alignItems="center" gap={1.5}>
                <HeaderCell>{t('fleet-management-field-dealer')}</HeaderCell>
                <Box sx={METER_COLUMN} />
                <HeaderCell width={COUNT_COLUMN}>{t('fleet-management-column-robots')}</HeaderCell>
              </Stack>
            )}
            {dealerStats.byCount.slice(0, 15).map((d) => renderCountRow(d))}
            <Collapse in={countExpanded}>
              <Stack gap={1}>{dealerStats.byCount.slice(15).map((d) => renderCountRow(d))}</Stack>
            </Collapse>
            {dealerStats.byCount.slice(15).length > 0 && (
              <Button
                variant="text"
                size="small"
                endIcon={countExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={expandByCountListClickHandler}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t(countExpanded ? 'fleet-management-show-less' : 'fleet-management-show-all')}
              </Button>
            )}
          </Stack>
        </DashboardSection>
      </Stack>
    </Stack>
  );
};

// The datasets are memoized upstream, so a filter change that does not touch them costs
// nothing here.
export default React.memo(DealerOverviewWidgets);
